import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtDate } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Printer, RefreshCw, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const SIZES = ["100x50", "100x75", "70x40", "50x30"];

export function LabelPreview({ receiptId }) {
  const [data, setData] = useState(null);
  const [size, setSize] = useState("100x50");
  const [count, setCount] = useState(1);
  const [history, setHistory] = useState([]);

  async function load() {
    const { data } = await api.get(`/receipts/${receiptId}/label`);
    setData(data);
    const h = await api.get(`/receipts/${receiptId}/label/history`);
    setHistory(h.data);
  }
  useEffect(() => { load(); }, [receiptId]);

  async function doPrint(reprint) {
    const { data: res } = await api.post(`/receipts/${receiptId}/label/print`, { size, count: parseInt(count) || 1, reprint });
    if (res.warning) toast.warning(res.warning);
    toast.success(`Pencatatan cetak label tersimpan (${count}x).`);
    load();
    setTimeout(() => window.print(), 300);
  }

  if (!data) return <div className="text-muted-foreground">Memuat label...</div>;
  const [w, h] = size.split("x").map(Number);

  return (
    <div className="grid lg:grid-cols-2 gap-5">
      <div>
        {!data.approved && (
          <div className="flex items-start gap-2 p-3 mb-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-700 text-sm">
            <AlertTriangle className="w-4 h-4 mt-0.5" /> Batch belum DITERIMA (belum disetujui QC). Label tetap dapat dicetak namun ditandai belum disetujui.
          </div>
        )}
        <div id="label-print-area" className="bg-white border-2 border-black mx-auto text-black p-3"
          style={{ width: `${w * 3}px`, minHeight: `${h * 3}px` }} data-testid="label-canvas">
          <div className="text-center font-bold text-sm border-b border-black pb-1">{data.company}</div>
          <div className="mt-1 text-[11px] leading-tight">
            <div className="font-bold text-sm">{data.material_name}</div>
            <div>Kode: {data.material_code}</div>
            <div className="font-mono font-bold text-sm my-1">{data.internal_batch}</div>
            {data.tracking_no && <div className="font-mono" data-testid="label-tracking">No. Tracking: <b>{data.tracking_no}</b></div>}
            <div>Batch Pemasok: {data.supplier_batch || "-"}</div>
            <div>Terima: {fmtDate(data.tgl_terima)} • ED: {fmtDate(data.expiry_date)}</div>
            <div>Lokasi: {data.location || "-"}</div>
            <div className="mt-1"><span className={`px-2 py-0.5 rounded text-[10px] font-bold ${data.approved ? "bg-emerald-600 text-white" : "bg-amber-500 text-white"}`}>{data.status_label}</span></div>
          </div>
          <div className="flex items-center justify-between mt-2 gap-2">
            <img src={data.qr} alt="QR" className="w-16 h-16" />
            <img src={data.barcode} alt="Barcode" className="h-12 flex-1 object-contain" />
          </div>
        </div>
      </div>

      <Card className="p-4 space-y-3 print:hidden">
        <h4 className="font-heading font-bold text-sm">Pengaturan Cetak Thermal</h4>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Ukuran Label (mm)</Label>
            <Select value={size} onValueChange={setSize}><SelectTrigger data-testid="label-size" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>{SIZES.map((s) => <SelectItem key={s} value={s}>{s} mm</SelectItem>)}</SelectContent></Select></div>
          <div><Label className="text-xs">Jumlah Label</Label><Input data-testid="label-count" type="number" min="1" value={count} onChange={(e) => setCount(e.target.value)} className="mt-1" /></div>
        </div>
        <div className="flex gap-2">
          <Button data-testid="label-print" onClick={() => doPrint(false)}><Printer className="w-4 h-4 mr-1" /> Cetak Label</Button>
          <Button variant="outline" data-testid="label-reprint" onClick={() => doPrint(true)}><RefreshCw className="w-4 h-4 mr-1" /> Cetak Ulang</Button>
        </div>
        <p className="text-[11px] text-muted-foreground">
          Pencetakan menggunakan dialog cetak browser (kompatibel printer thermal Windows via driver). Cetak ulang memakai identitas batch yang sama — tidak membuat batch baru.
          Integrasi USB/jaringan langsung bergantung pada driver &amp; model printer dan belum dijamin tersedia otomatis.
        </p>
        <div>
          <h5 className="text-xs font-semibold mb-2">Riwayat Pencetakan</h5>
          <div className="space-y-1 max-h-48 overflow-y-auto">
            {history.map((hh) => (
              <div key={hh.id} className="text-xs flex justify-between border-b border-slate-100 py-1">
                <span>{hh.size} • {hh.count}x {hh.reprint ? "(ulang)" : ""}</span>
                <span className="text-muted-foreground">{hh.printed_by}</span>
              </div>
            ))}
            {history.length === 0 && <div className="text-xs text-muted-foreground">Belum ada pencetakan.</div>}
          </div>
        </div>
      </Card>
    </div>
  );
}

export default function Labels() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [sel, setSel] = useState(null);
  useEffect(() => { api.get("/receipts").then((r) => setRows(r.data)); }, []);

  return (
    <div className="space-y-5">
      <div><h1 className="font-heading text-2xl font-extrabold">Cetak Label Thermal</h1>
        <p className="text-sm text-muted-foreground">Pilih penerimaan untuk membuat label batch dengan QR &amp; barcode.</p></div>
      <div className="grid lg:grid-cols-3 gap-5">
        <Card className="p-3 lg:col-span-1 max-h-[70vh] overflow-y-auto">
          {rows.map((r) => (
            <button key={r.id} data-testid={`label-pick-${r.internal_batch}`} onClick={() => setSel(r)}
              className={`w-full text-left p-2.5 rounded-lg mb-1 text-sm transition-colors ${sel?.id === r.id ? "bg-primary text-primary-foreground" : "hover:bg-accent"}`}>
              <div className="flex items-center justify-between"><span className="batch-chip">{r.internal_batch}</span><StatusBadge status={r.status} /></div>
              <div className="mt-1">{r.material_name}</div>
            </button>
          ))}
          {rows.length === 0 && <div className="text-sm text-muted-foreground p-3">Belum ada penerimaan.</div>}
        </Card>
        <div className="lg:col-span-2">
          {sel ? <LabelPreview receiptId={sel.id} /> : <Card className="p-10 text-center text-muted-foreground">Pilih penerimaan di kiri untuk melihat pratinjau label.</Card>}
        </div>
      </div>
    </div>
  );
}
